import React, { useState, useRef, useEffect, useCallback } from 'react';
import { parseFaqMarkdown } from '../../utils/parseFaqMarkdown';
import FeedbackModal from '../feedback/FeedbackModal';

const MENU_ITEMS = [
  {
    id: 'help',
    label: 'Help & FAQ',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
  },
  {
    id: 'feedback',
    label: 'Send Feedback',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z" />
        <path d="M12 8v4" />
        <path d="M12 16h.01" />
      </svg>
    ),
  },
];

// Category display order
const CATEGORY_ORDER = [
  'Why Chaggerheart?',
  'Getting Started',
  'Character Builder',
  'Gameplay Tools',
  'Campaigns & GM Tools',
  'Troubleshooting',
  'Beta Program',
];

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-gray-700/30">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3 px-2 text-left hover:text-red-400 transition-colors duration-200"
      >
        <span className="text-gray-200 text-sm font-medium pr-3">{question}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`text-gray-500 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="pb-3 px-2 text-gray-400 text-xs leading-relaxed whitespace-pre-line">
          {answer}
        </div>
      )}
    </div>
  );
}

function FAQPanel({ onBack }) {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetch('/data/faq-content.md')
      .then((res) => res.text())
      .then((md) => setFaqs(parseFaqMarkdown(md)))
      .catch((err) => console.error('Failed to load FAQs:', err))
      .finally(() => setLoading(false));
  }, []);

  const filtered = search.trim()
    ? faqs.filter(
        (f) =>
          f.question.toLowerCase().includes(search.toLowerCase()) ||
          f.answer.toLowerCase().includes(search.toLowerCase())
      )
    : faqs;

  // Group by category in defined order
  const grouped = filtered.reduce((acc, faq) => {
    const cat = faq.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(faq);
    return acc;
  }, {});

  const sortedCategories = Object.keys(grouped).sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  return (
    <div className="w-[340px] max-h-[65vh] flex flex-col bg-gradient-to-br from-gray-900 to-black rounded-md shadow-2xl border-2 border-red-700 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-700/50">
        <button
          onClick={onBack}
          className="p-1 text-gray-400 hover:text-white transition-colors"
          aria-label="Back to menu"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <span className="text-sm font-semibold text-gray-100">Help & FAQ</span>
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-gray-700/30">
        <input
          type="text"
          placeholder="Search FAQs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-gray-800/60 border border-gray-700/50 rounded px-2.5 py-1.5 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-red-700/50"
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {loading ? (
          <div className="text-gray-500 py-6 text-center text-sm">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-gray-500 py-6 text-center text-sm">
            {search ? 'No matching FAQs found.' : 'No FAQs available yet.'}
          </div>
        ) : (
          sortedCategories.map((category) => (
            <div key={category} className="mb-3">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 px-1">
                {category}
              </h3>
              <div>
                {grouped[category].map((faq) => (
                  <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/**
 * Help button for the app header. Opens a popover menu with inline FAQ panel and feedback options.
 */
export default function HelpButton() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [modalTab, setModalTab] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setShowFAQ(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const handleItemClick = useCallback((item) => {
    if (item.id === 'help') {
      setShowFAQ(true);
    } else {
      setMenuOpen(false);
      setShowFAQ(false);
      setModalTab(item.id);
    }
  }, []);

  const handleBack = useCallback(() => {
    setShowFAQ(false);
  }, []);

  return (
    <>
      <div ref={menuRef} className="relative">
        <button
          onClick={() => {
            setMenuOpen(!menuOpen);
            if (menuOpen) setShowFAQ(false);
          }}
          className="p-1.5 sm:p-2 text-gray-400 hover:text-red-400 transition-colors duration-200"
          aria-label="Help and feedback"
          title="Help"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </button>

        {menuOpen && (
          <div className="absolute right-0 mt-2 z-50">
            {showFAQ ? (
              <FAQPanel onBack={handleBack} />
            ) : (
              <div className="w-48 bg-gradient-to-br from-gray-900 to-black rounded-md shadow-2xl border-2 border-red-700 overflow-hidden">
                <div className="py-1">
                  {MENU_ITEMS.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleItemClick(item)}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-red-900 hover:text-white transition-colors duration-200 text-left"
                    >
                      <span className="text-gray-400">{item.icon}</span>
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {modalTab && (
        <FeedbackModal
          initialTab={modalTab}
          onClose={() => setModalTab(null)}
        />
      )}
    </>
  );
}
